import { inject, Injectable, signal } from '@angular/core';
import { Observable, of, tap } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { map } from 'rxjs/operators';
import { CategoryModel } from '../models/category.model';
import { ProductModel } from '../models/product.model';
import { CartItem } from '../models/cart-item.model';

export interface OrderPayload {
  name: string;
  phone: string;
  city: string;
  branch: string;
  payment: 'prepayment' | 'after_delivery';
  comment: string;
  items: CartItem[];
}

@Injectable({
  providedIn: 'root'
})
export class ShopService {
  protected http: HttpClient = inject(HttpClient);

  private readonly apiUrl = `${environment.apiUrl}/front/shop`;

  categories = signal<CategoryModel[]>([]);

  getCategories(refresh: boolean = false): Observable<CategoryModel[]> {
    if (this.categories().length && !refresh) {
      return of(this.categories());
    }

    return this.http.get<CategoryModel[]>(`${this.apiUrl}/category`).pipe(
      map((response) => {
        return response.map(itm => {
          itm = new CategoryModel(itm);
          return itm;
        });
      }),
      tap((items) => {
        this.categories.set(items);
      })
    );
  }

  getProductsByIds(ids: number[]): Observable<ProductModel[]> {
    return this.http.get<ProductModel[]>(`${this.apiUrl}/product/batch`, {
      params: { ids: ids.join(',') }
    }).pipe(
      map(response => response.map(itm => new ProductModel(itm)))
    );
  }

  getProduct(slug: string): Observable<ProductModel> {
    return this.http.get<ProductModel>(`${this.apiUrl}/product/${slug}`).pipe(
      map(response => new ProductModel(response))
    );
  }

  getProducts(params: any = {}): Observable<{ count: number, data: ProductModel[] }> {
    const { filters, ...rest } = params;
    const httpParams: Record<string, any> = { ...rest };
    if (filters && Object.keys(filters).length) {
      httpParams['filter'] = JSON.stringify(filters);
    }
    return this.http.get<{ count: number, data: ProductModel[] }>(`${this.apiUrl}/product`, {
      params: httpParams
    }).pipe(
      map((response: { count: number, data: ProductModel[] }) => {
        return {
          count: response.count,
          data: response.data.map(itm => {
            itm = new ProductModel(itm);
            return itm;
          })
        };
      }),
      tap((data) => {
        return data;
      })
    );
  }

  create(payload: OrderPayload) {
    return this.http.post(`${this.apiUrl}/order`, payload);
  }

}
